import { parameterizePath, scrubSensitiveUrl } from './analyticsSanitization';

const AUTH_TOKEN_VALUE = /((?:secureToken|secure_token)=)[^&#\s]*/g;

function containsAuthTokenParam(raw: string): boolean {
  return raw.includes('secureToken') || raw.includes('secure_token');
}

/**
 * Strip magic-link token values from free text (console and UI breadcrumb
 * messages). URL parsing is wrong here: a message like "Failed to load
 * patients" is not a URL, and the URL parser rewrites it.
 *
 * Only touches strings that actually contain `secureToken` or `secure_token`.
 */
export function scrubAuthTokensFromText(raw: string): string {
  if (!containsAuthTokenParam(raw)) return raw;
  return raw.replace(AUTH_TOKEN_VALUE, '$1[Filtered]');
}

/** A path or absolute URL, safe to run through scrubSensitiveUrl. */
function isUrlLike(raw: string): boolean {
  return raw.startsWith('/') || raw.startsWith('http://') || raw.startsWith('https://');
}

/**
 * Scrub a string that may be a URL or free text carrying a token.
 * URL-like values use scrubSensitiveUrl (drop query/hash + patient ids).
 * Non-URL strings keep plain text intact unless they embed a token param.
 */
function scrubAuthTokenBearingString(raw: string): string {
  if (isUrlLike(raw)) return scrubSensitiveUrl(raw);
  return parameterizePath(scrubAuthTokensFromText(raw));
}

function scrubStringRecord(data: Record<string, unknown> | undefined) {
  if (!data) return;
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = scrubAuthTokenBearingString(value);
    }
  }
}

type SpanLike = {
  description?: string;
  data?: Record<string, unknown>;
};

type TransactionLike = {
  transaction?: string;
  request?: {
    url?: string;
    query_string?: unknown;
  };
  spans?: SpanLike[];
  contexts?: {
    trace?: {
      data?: Record<string, unknown>;
    };
  };
};

/**
 * Scrub magic-link tokens and patient ids from a Sentry transaction
 * before it is sent.
 *
 * Pageload transactions capture `window.location` before Login's
 * `replaceState` runs, so legacy `?secureToken=` links can still land in
 * the transaction name, request URL, and span data.
 */
export function scrubAuthTokensFromTransaction<T extends TransactionLike>(event: T): T {
  if (typeof event.transaction === 'string') {
    event.transaction = scrubAuthTokenBearingString(event.transaction);
  }
  if (event.request?.url) {
    event.request.url = scrubSensitiveUrl(event.request.url);
  }
  if (event.request && 'query_string' in event.request) {
    delete event.request.query_string;
  }
  if (event.spans) {
    for (const span of event.spans) {
      if (typeof span.description === 'string') {
        span.description = scrubAuthTokenBearingString(span.description);
      }
      scrubStringRecord(span.data);
    }
  }
  scrubStringRecord(event.contexts?.trace?.data);
  return event;
}

/**
 * Strip magic-link auth tokens from a URL's query string and hash while
 * preserving other params. Used by Login's history.replaceState scrub so
 * the address bar stays usable (lang, etc.) without the token.
 *
 * Sentry telemetry uses `scrubSensitiveUrl` instead (drop all query/hash).
 */
export function scrubAuthTokensFromUrl(raw: string): string {
  try {
    const absolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw);
    const url = absolute ? new URL(raw) : new URL(raw, 'http://scrub.local');
    url.searchParams.delete('secureToken');
    url.searchParams.delete('secure_token');

    const hashBody = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    if (hashBody) {
      const hashParams = new URLSearchParams(hashBody);
      if (hashParams.has('secureToken') || hashParams.has('secure_token')) {
        hashParams.delete('secureToken');
        hashParams.delete('secure_token');
        const next = hashParams.toString();
        url.hash = next ? `#${next}` : '';
      }
    }

    if (absolute) return url.toString();
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return raw;
  }
}
